import React, { useContext, useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import { useFetchApi } from "../../../hooks/useFetchApi";
import SLLogo from "../../../icons/srilanka.svg";
import QatarLogo from "../../../icons/qutar.svg";
import SaudiLogo from "../../../icons/saudi.svg";
import { DispatchContext } from "../../../store/context";
import { UPDATE_BASE_CURRENCY } from "../../../store/types";

interface StockSearchModalProps {
  loading?: boolean;
  selectedStock: any;
  setSelectedStock: (value: any) => void;
}

export const StockSelectOptions: React.FC<StockSearchModalProps> = ({
  loading = false,
  selectedStock,
  setSelectedStock,
}) => {
  const [stocks, setStocks] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [selectedExchange, setSelectedExchange] = useState<string>("all");

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  // Fetch stocks
  useEffect(() => {
    getStocks();
  }, []);

  // Process stocks
  useEffect(() => {
    if (response && Object.keys(response).length) {
      const cseStocks =
        response?.find(
          (section: any) => section?.section === "Colombo Stock Exchange"
        )?.options || [];

      const activeStocks = cseStocks.filter((stock: any) => stock.status === 1);

      setStocks(activeStocks);

      // Set default stock to JKH.N if no stock is already selected
      if (!selectedStock) {
        const defaultStock = activeStocks.find(
          (stock: any) => stock.ticker_symbol === "JKH.N"
        );
        if (defaultStock) {
          dispatch({ type: UPDATE_BASE_CURRENCY, payload: "LKR" });
          setSelectedStock(defaultStock);
        }
      }
    }
  }, [response]);

  // Get unique stock exchanges from stocks
  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    stocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });
    return Array.from(exchanges).sort();
  }, [stocks]);

  // Helper function to get exchange logo and short name
  const getExchangeInfo = (exchangeName: string) => {
    switch (exchangeName) {
      case "Colombo Stock Exchange":
        return { logo: SLLogo, short: "CSE" };
      case "Qatar Stock Exchange":
        return { logo: QatarLogo, short: "QSE" };
      case "Saudi Stock Exchange":
        return { logo: SaudiLogo, short: "SSE" };
      default:
        return { logo: null, short: "ALL" };
    }
  };

  useEffect(() => {
    if (stocks && stocks.length > 0) {
      // Filter stocks based on selected exchange
      const filteredStocks =
        selectedExchange === "all"
          ? stocks
          : stocks.filter((stock) => stock.market_name === selectedExchange);

      const formattedOptions = filteredStocks.map((stock) => ({
        value: stock.id,
        label: `${stock.ticker_symbol} ${stock.company_name}`,
        stock: stock,
      }));
      setOptions(formattedOptions);
    }
  }, [stocks, selectedExchange]);

  const dispatch = useContext(DispatchContext);

  const handleChange = (value: number) => {
    if (!value) {
      setSelectedStock(null);
      return;
    }

    const selected = stocks.find((stock) => stock.id === value);

    const currency =
      selected?.short_name === "CSE"
        ? "LKR"
        : selected.short_name === "QSE"
        ? "QAR"
        : selected.short_name === "SSE"
        ? "SAR"
        : "USD";

    dispatch({ type: UPDATE_BASE_CURRENCY, payload: currency });

    setSelectedStock(selected);
  };

  const optionRender = (option: any) => {
    const stock = stocks.find((s) => s.id === option.data.value);

    if (!stock) return option.label;

    return (
      <div className="flex flex-col w-full">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-x-2">
            <img
              src={`/${stock.company_logo}`}
              alt={stock.company_logo}
              className="w-6 h-6 object-contain"
            />
            <span className="font-dm text-sm text-[#344054]">
              {stock.company_name}
            </span>
          </div>
          <div className="flex items-center gap-x-2">
            <img
              src={
                stock?.market_name === "Colombo Stock Exchange"
                  ? SLLogo
                  : stock?.market_name === "Qatar Stock Exchange"
                  ? QatarLogo
                  : SaudiLogo
              }
              alt=""
              className="w-6 h-6 object-cover"
            />
            <span className="text-[#344054] font-dm text-sm">
              {stock.short_name}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const labelRender = (props: any) => {
    const { value } = props;
    const stock = stocks.find((s) => s.id === value);

    if (!stock) return value;

    const changeColor =
      stock.change_perc >= 0 ? "text-green-600" : "text-red-600";
    const changeSign = stock.change_perc >= 0 ? "+" : "";

    return (
      <div className="flex items-center gap-2">
        <span className="text-gray-900 dark:text-gray-100">{stock.company_name}</span>
        <span className="text-gray-700 dark:text-gray-500">•</span>
        <span className="text-gray-900 dark:text-gray-100">{stock.ticker_symbol}</span>
        <span className="text-gray-700 dark:text-gray-500">•</span>
        <span className="text-gray-900 dark:text-gray-100">{stock.closed}</span>
        <span className={`font-medium ${changeColor}`}>
          ({changeSign}
          {stock.change_perc}%)
        </span>
      </div>
    );
  };

  return (
    <div
      className="relative !w-full
        dark:[&_.ant-select-selector]:!bg-[#0d1420]
        dark:[&_.ant-select-selector]:!border-[rgba(148,163,184,0.16)]
        dark:[&_.ant-select-selection-placeholder]:!text-[#6b7a94]
        dark:[&_.ant-select-arrow]:!text-[#6b7a94]
        dark:[&_.ant-select-clear]:!bg-[#0d1420]
        dark:[&_.ant-select-clear]:!text-[#6b7a94]"
    >
      {/* Stock Select Dropdown */}
      <Select
        showSearch
        allowClear
        placeholder="Search by ticker or company name"
        style={{ width: "100%", height: 40 }}
        value={selectedStock?.id || undefined}
        onChange={handleChange}
        loading={stockLoading}
        optionFilterProp="label"
        options={options}
        optionRender={optionRender}
        labelRender={labelRender}
        popupClassName="vi-ta-stock-popup"
        notFoundContent={
          stockLoading ? (
            <div className="p-3 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="p-3 text-center text-gray-500">No stocks found</div>
          )
        }
      />
    </div>
  );
};
